import type { OwaDate } from "owa-datetime";
import { getDate, getHours, getISOString, getMinutes, getMonth, getSeconds, getTimestamp, getYear, owaDate, startOfDay, userDate, utcDate, } from "owa-datetime";

/**
 * Flag start/due/complete dates carry the calendar day, and for a custom pick the wall-clock time,
 * that the user chose, anchored in UTC. This is the MAPI convention shared with Outlook desktop and
 * mobile, extended to carry a time.
 *
 * Anchoring every flag date in UTC keeps them all in one frame, so the server's sort order, the
 * group header buckets and the rendered value agree regardless of the reader's time zone. The flag
 * mutations pin their requests to a UTC TimeZoneContext so the value round-trips unshifted.
 *
 * Reads are not pinned: they inherit the mailbox time zone, so the service renders the stored
 * instant in that zone. A value written as `2026-09-11T00:00:00.000Z` comes back to a Central
 * reader as `2026-09-10T19:00:00-05:00`. Both are the same instant, and its UTC fields are the day
 * and time the user picked, so every flag date must be read as an instant rather than as the wall
 * clock the service happened to print.
 */

/**
 * Reads a flag date as an instant, whose UTC fields are the picked day and time. Honouring the
 * offset rather than neutralizing it means a value the service hands back as
 * `2026-09-10T19:00:00-05:00` reads as September 11 at 00:00, the day it was flagged.
 */
export function parseFlagDate(flagDate: string): OwaDate {
    return utcDate(flagDate);
}

/**
 * Re-anchors a flag date to the user's time zone without moving the day and time it encodes, so it
 * can be compared against, or edited alongside, dates that already live in that time zone.
 */
export function flagDateInUserTimeZone(flagDate: string): OwaDate {
    const parsed = parseFlagDate(flagDate);
    return owaDate(userDate(), getYear(parsed), getMonth(parsed), getDate(parsed), getHours(parsed), getMinutes(parsed), getSeconds(parsed));
}

/**
 * Returns the start of the flag date's calendar day in the user's time zone, so day and week
 * bucketing compares it against the user's own "today" rather than an instant in another frame.
 */
export function startOfFlagDateDay(flagDate: string): OwaDate {
    return startOfDay(flagDateInUserTimeZone(flagDate));
}

/** Encodes a date-only flag date, used by the quick flag options and CompleteDate. */
export function toFlagDayIso(date: OwaDate): string {
    return getISOString(utcDate(getYear(date), getMonth(date), getDate(date)));
}

/** Encodes a custom flag date, preserving the picked wall-clock time. */
export function toFlagDateTimeIso(date: OwaDate): string {
    return getISOString(utcDate(getYear(date), getMonth(date), getDate(date), getHours(date), getMinutes(date), getSeconds(date)));
}

/**
 * Compares two flag dates by the instant they encode, so neither differing ISO precision between
 * the client (`.000Z`) and the service (`Z`) nor the offset the service renders them with is
 * reported as a change.
 */
export function isSameFlagDate(left: string | null, right: string | null): boolean {
    if (!left || !right) {
        return !left && !right;
    }
    return getTimestamp(parseFlagDate(left)) === getTimestamp(parseFlagDate(right));
}

/**
 * Orders two flag dates by the instant they encode. A row flagged in this session holds the value
 * the client wrote while rows loaded from the service hold the same instant rendered in the
 * mailbox time zone, so ordering the raw strings would place equal dates apart, and an earlier
 * date after a later one. Absent values are treated as equal; callers bucket those separately.
 */
export function compareFlagDates(left: string | null, right: string | null): number {
    if (!left || !right) {
        return 0;
    }
    return getTimestamp(parseFlagDate(left)) - getTimestamp(parseFlagDate(right));
}
